/**
 * useStartFollowupInvestigation — submit a follow-up question against a
 * completed investigation.
 *
 * Behaviour:
 *   1. Opens the SSE stream via investigate.stream({mode: 'live', body})
 *      with parent_investigation_id pre-filled.
 *   2. Waits for the FIRST `anchor` event (which carries the new
 *      investigation_id) — that's our cue that the runner has persisted
 *      the row and started the agentic loop.
 *   3. Aborts the stream and resolves the submit promise with the new
 *      investigation_id. The runner keeps going server-side; the caller
 *      navigates to /investigations/{id} which renders via the
 *      catchup-stream path.
 *   4. If the backend returns an HTTP error (e.g. 422 for an invalid
 *      parent), surface it as an error state — the page renders an
 *      inline alert.
 *
 * Why this shape: the live SSE path is the single endpoint we have for
 * starting investigations, but the detail page is the natural rendering
 * surface (it already handles catchup + tail). So we use the SSE
 * endpoint just long enough to discover the new id, then hand off.
 */

import { useCallback, useRef, useState } from 'react';

import { investigate } from '@/helpers/ApiHelper';
import type { InvestigateRequest } from '@/types/investigation';

export type FollowupStatus = 'idle' | 'submitting' | 'error';

export interface FollowupState {
  status: FollowupStatus;
  error: { status?: number; message: string } | null;
}

export interface UseStartFollowupInvestigation {
  state: FollowupState;
  submit: (prompt: string, parentInvestigationId: string) => Promise<string>;
  reset: () => void;
}

const initialState: FollowupState = {
  status: 'idle',
  error: null,
};

export function useStartFollowupInvestigation(): UseStartFollowupInvestigation {
  const [state, setState] = useState<FollowupState>(initialState);
  const teardownRef = useRef<(() => void) | null>(null);

  const reset = useCallback(() => {
    if (teardownRef.current) {
      teardownRef.current();
      teardownRef.current = null;
    }
    setState(initialState);
  }, []);

  const submit = useCallback(
    (prompt: string, parentInvestigationId: string): Promise<string> => {
      // Tear down any in-flight follow-up before starting a new one.
      if (teardownRef.current) {
        teardownRef.current();
        teardownRef.current = null;
      }
      setState({ status: 'submitting', error: null });

      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const body: InvestigateRequest = {
        prompt: prompt.trim(),
        tz,
        parent_investigation_id: parentInvestigationId,
      };

      return new Promise<string>((resolve, reject) => {
        let resolved = false;

        teardownRef.current = investigate.stream(
          { mode: 'live', body },
          {
            onEvent: (evt) => {
              // The first `anchor` event is enough — we have the new id.
              if (evt.event === 'anchor' && !resolved) {
                resolved = true;
                const newId = evt.data.investigation_id;
                // Tear down the stream we no longer need.
                if (teardownRef.current) {
                  teardownRef.current();
                  teardownRef.current = null;
                }
                setState(initialState);
                resolve(newId);
              }
            },
            onHttpError: ({ status, detail }) => {
              if (resolved) return;
              resolved = true;
              setState({
                status: 'error',
                error: { status, message: detail },
              });
              reject(new Error(detail));
            },
            onStreamError: (err) => {
              if (resolved) return;
              resolved = true;
              setState({
                status: 'error',
                error: { message: err.message },
              });
              reject(err);
            },
            onComplete: () => {
              // If the stream closed without an anchor or an HTTP error,
              // surface a generic failure.
              if (resolved) return;
              resolved = true;
              setState({
                status: 'error',
                error: { message: 'Stream ended before the follow-up started' },
              });
              reject(new Error('Stream ended before the follow-up started'));
            },
          },
        );
      });
    },
    [],
  );

  return { state, submit, reset };
}
